import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export const fetchCoupons = async (params = {}) => {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );
  const { data } = await api.get("/coupons", { params: cleaned });
  return data;
};

export const fetchCoupon = async (id) => {
  const { data } = await api.get(`/coupons/${id}`);
  return data;
};

export const fetchStoreBySlug = async (slug) => {
  const { data } = await api.get(`/stores/by-slug/${encodeURIComponent(slug)}`);
  return data;
};

export const redeemCoupon = async (id) => {
  const { data } = await api.post(`/coupons/${id}/redeem`);
  return data;
};

export const voteCoupon = async (id, dir) => {
  const { data } = await api.post(`/coupons/${id}/vote`, null, { params: { dir } });
  return data;
};

export const fetchCategories = async () => {
  const { data } = await api.get("/categories");
  return data;
};

export const fetchStores = async () => {
  const { data } = await api.get("/stores");
  return data;
};

export const fetchStats = async () => {
  const { data } = await api.get("/coupons/stats");
  return data;
};

export const fetchMe = async () => {
  const { data } = await api.get("/auth/me");
  return data;
};

export const postSession = async (session_id) => {
  const { data } = await api.post("/auth/session", { session_id });
  return data;
};

export const logout = async () => {
  const { data } = await api.post("/auth/logout");
  return data;
};

export const fetchFavoriteIds = async () => {
  const { data } = await api.get("/favorites/ids");
  return data;
};

export const fetchFavorites = async () => {
  const { data } = await api.get("/favorites");
  return data;
};

export const addFavorite = async (id) => {
  const { data } = await api.post(`/favorites/${id}`);
  return data;
};

export const removeFavorite = async (id) => {
  const { data } = await api.delete(`/favorites/${id}`);
  return data;
};

export const submitCoupon = async (payload) => {
  const { data } = await api.post("/coupons", payload);
  return data;
};

// ---- Referrals ----
export const getReferralInfo = async () => {
  const { data } = await api.get("/referrals/me");
  return data;
};
export const claimBonusPack = async () => {
  const { data } = await api.post("/referrals/claim-pack");
  return data;
};
export const applyReferralCode = async (code) => {
  const { data } = await api.post("/referrals/apply", { code });
  return data;
};

// ---- Email/Password Auth ----
export const registerWithEmail = async (email, password, name, ref) => {
  const payload = { email, password };
  if (name) payload.name = name;
  if (ref) payload.ref = ref;
  const { data } = await api.post("/auth/register", payload);
  return data;
};
export const loginWithEmail = async (email, password) => {
  const { data } = await api.post("/auth/login", { email, password });
  return data;
};
export const forgotPassword = async (email) => {
  const { data } = await api.post("/auth/forgot-password", { email });
  return data;
};
export const resetPassword = async (token, password) => {
  const { data } = await api.post("/auth/reset-password", { token, password });
  return data;
};

// ---- Admin: Sponsored Slots ----
export const adminCheck = async () => {
  const { data } = await api.get("/admin/check");
  return data;
};

export const adminSearchCoupons = async (params = {}) => {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );
  const { data } = await api.get("/admin/coupons/search", { params: cleaned });
  return data;
};

export const adminListSponsored = async () => {
  const { data } = await api.get("/admin/sponsored");
  return data;
};

export const adminSponsor = async (id, payload) => {
  const { data } = await api.post(`/admin/coupons/${id}/sponsor`, payload);
  return data;
};

export const adminUnsponsor = async (id) => {
  const { data } = await api.post(`/admin/coupons/${id}/unsponsor`);
  return data;
};

export const adminStats = async () => {
  const { data } = await api.get("/admin/stats");
  return data;
};

export const adminRevenue = async () => {
  const { data } = await api.get("/admin/revenue");
  return data;
};

// ---- Click tracking ----
export const logClick = async (id) => {
  try {
    await api.post(`/clicks/${id}`);
  } catch {
    // Intentionally silent — analytics failure must not break UX
  }
};

export const adminClickStats = async (days = 30) => {
  const { data } = await api.get("/admin/clicks/stats", { params: { days } });
  return data;
};

// ---- Digest email ----
export const getDigestPref = async () => {
  const { data } = await api.get("/digest/preferences");
  return data;
};
export const setDigestPref = async (opt_in) => {
  const { data } = await api.post("/digest/preferences", { opt_in });
  return data;
};
export const sendTestDigest = async () => {
  const { data } = await api.post("/digest/send-test");
  return data;
};

// ---- Sponsor self-serve ----
export const getSponsorTiers = async () => {
  const { data } = await api.get("/sponsor/tiers");
  return data;
};
export const startSponsorCheckout = async (payload) => {
  const { data } = await api.post("/sponsor/checkout", payload);
  return data;
};
export const getSponsorStatus = async (session_id) => {
  const { data } = await api.get(`/sponsor/status/${session_id}`);
  return data;
};

// ---- Deal of the Day ----
export const fetchDealOfTheDay = async () => {
  const { data } = await api.get("/deal-of-the-day");
  return data;
};

// ---- Best Deals topics (programmatic SEO pages) ----
export const fetchBestDealsTopics = async () => {
  const { data } = await api.get("/best-deals/topics");
  return data;
};
export const fetchBestDealsTopic = async (topic, limit = 60) => {
  const { data } = await api.get(`/best-deals/${topic}`, { params: { limit } });
  return data;
};

// ---- Sponsored DOTD (retailer self-serve) ----
export const getDotdBooked = async () => {
  const { data } = await api.get("/sponsor/dotd/booked");
  return data;
};
export const startDotdCheckout = async (payload) => {
  const { data } = await api.post("/sponsor/dotd/checkout", payload);
  return data;
};
export const getDotdKit = async (session_id) => {
  const { data } = await api.get(`/sponsor/dotd/kit/${session_id}`);
  return data;
};

// ---- Store watchlist (price-drop alerts) ----
export const listWatchlist = async () => {
  const { data } = await api.get("/watchlist");
  return data;
};
export const addWatchlist = async (store) => {
  const { data } = await api.post("/watchlist", { store });
  return data;
};
export const removeWatchlist = async (store) => {
  const { data } = await api.delete("/watchlist", { params: { store } });
  return data;
};
export const checkWatchlist = async (store) => {
  const { data } = await api.get("/watchlist/check", { params: { store } });
  return data;
};
